import { caracasDay, extraUsersFrequencyFactor, getExtraUserPriceUsd, mixExtraUsersMonths } from './extra-users.util';

const MIX_IVA_RATE = 0.16;

export type MixLineType = 'sms' | 'controlNumbers' | 'users' | 'setup';

/**
 * Etiquetas de las líneas del carrito mix. Fuente única para el aside de «Mejora tu Fina»,
 * el paso 2 de `fina-form-pay-plan` y el historial de pagos (`mix-breakdown.util`).
 */
export const MIX_LINE_LABELS: Record<MixLineType, string> = {
    sms: 'SMS',
    controlNumbers: 'Números de control',
    users: 'Usuarios extra',
    setup: 'Inducción personalizada',
};

/** Unidad que acompaña a la cantidad («100 SMS», «2 usuarios extra»). */
const MIX_LINE_UNITS: Record<MixLineType, { one: string; other: string }> = {
    sms: { one: 'SMS', other: 'SMS' },
    controlNumbers: { one: 'número de control', other: 'números de control' },
    users: { one: 'usuario extra', other: 'usuarios extra' },
    setup: { one: 'inducción', other: 'inducciones' },
};

export interface MixQuoteSetupInput {
    baseAmountUSD?: number;
    ivaUSD?: number;
    retentionUSD?: number;
    payableUSD?: number;
}

export interface MixQuoteInput {
    smsPriceUsd?: number;
    /** Solo para `lines`: no participa en el precio. */
    smsQuantity?: number;
    /** Precio USD neto del paquete de números de control (ADM-732). */
    controlNumbersPriceUsd?: number;
    /** Solo para `lines`: no participa en el precio. */
    controlNumbersQuantity?: number;
    extraUsersToAdd?: number;
    extraUserPriceUsd?: number;
    includeSetup?: boolean;
    plus?: boolean;
    setup?: MixQuoteSetupInput | null;
    exchangeRateBCV?: number;
    /** Próxima fecha de pago: `bundle.nextPayment.dateToPay` tal cual (null en vitalicia). */
    referenceDate?: Date | string | null;
    /** `bundle.paymentFrequency`. */
    paymentFrequency?: string | null;
    /**
     * Instante con el que se cotizan los meses de cupos. El modal lo fija con `mixQuoteNow()`
     * (día del pago reportado en banco, reloj en tarjeta); sin él, el reloj.
     */
    now?: Date;
}

export interface MixQuoteLine {
    type: MixLineType;
    label: string;
    /** `null` cuando la línea no tiene cantidad (Setup) o el llamador no la pasó: no se pinta. */
    quantity: number | null;
    baseUsd: number;
}

export interface MixQuote {
    baseUSD: number;
    plusUSD: number;
    retentionUSD: number;
    amountUSD: number;
    amountVES: number;
    /** Líneas con base > 0, en el orden de api-admin: sms, controlNumbers, users, setup. */
    lines: MixQuoteLine[];
}

const round2 = (value: number): number => Number((Number(value) || 0).toFixed(2));

const positiveQuantity = (value: unknown): number | null => {
    const quantity = Math.floor(Number(value || 0));
    return quantity > 0 ? quantity : null;
};

/** Texto de la unidad para una línea con cantidad («números de control» / «número de control»). */
export function mixLineUnit(line: Pick<MixQuoteLine, 'type' | 'quantity'>): string {
    const unit = MIX_LINE_UNITS[line.type];
    return line.quantity === 1 ? unit.one : unit.other;
}

const CALENDAR_DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Día con el que se cotiza una mix por banco (tasa BCV y meses de cupos): el del pago
 * reportado, acotado a [ayer, hoy] en Caracas. `null` = hoy (cotiza con el reloj).
 *
 * Espejo de `resolveMixBankQuoteInstant` de api-admin (`mix-bill.helper.ts`), con sus mismos
 * vectores: si cambia una, cambia la otra en el mismo ciclo. `paidDay` es el día de calendario
 * del formulario ('YYYY-MM-DD'): se compara como string, nunca pasa por `caracasDay`.
 */
export function resolveMixBankQuoteDay(paidDay: string | null | undefined, now: Date): string | null {
    const today = caracasDay(now);
    if (!today || !paidDay || !CALENDAR_DAY.test(paidDay) || paidDay >= today) {
        return null;
    }
    const yesterday = caracasDay(new Date(`${today}T04:00:00.000Z`).getTime() - 24 * 60 * 60 * 1000);
    return yesterday && paidDay < yesterday ? yesterday : paidDay;
}

/** Instante de cotización de la mix por banco: el reloj hoy; si no, el inicio del día en Caracas (T04:00Z). */
export function mixBankQuoteInstant(paidDay: string | null | undefined, now: Date): Date {
    const day = resolveMixBankQuoteDay(paidDay, now);
    return day ? new Date(`${day}T04:00:00.000Z`) : now;
}

/**
 * Cotización mix alineada a api-admin `quoteMixPurchase`:
 * `amountUSD = baseUSD + plusUSD − retentionUSD`.
 * Los add-ons (SMS, números de control, usuarios) llevan IVA 16 % solo si `plus` y nunca retención.
 * La retención solo aplica a Setup y se toma de GET /plans (`retentionUSD` / `payableUSD`);
 * el front no recalcula un porcentaje de tenant.
 * `exchangeRateBCV` se usa tal cual: el mix no redondea la tasa (api-admin compara contra la oficial sin redondear).
 *
 * Cupos extra: `cupos × unidad × meses restantes × factor de la frecuencia`
 * (`mixExtraUsersMonths`, espejo de api-admin), con IVA solo si `plus` y sin retención.
 * Con la fecha de pago vencida (meses 0) los cupos no se cotizan: api-admin rechaza esa
 * compra con 409 `EXTRA_USERS_MIX_RENEWAL_DUE` y la UI la bloquea antes.
 */
export function quoteMixPurchase(input: MixQuoteInput): MixQuote {
    const plus = input.plus === true;
    let addOnBaseUSD = 0;
    let addOnPlusUSD = 0;
    const lines: MixQuoteLine[] = [];

    const smsPrice = round2(Number(input.smsPriceUsd || 0));
    if (smsPrice > 0) {
        addOnBaseUSD = round2(addOnBaseUSD + smsPrice);
        addOnPlusUSD = round2(addOnPlusUSD + (plus ? round2(smsPrice * MIX_IVA_RATE) : 0));
        lines.push({ type: 'sms', label: MIX_LINE_LABELS.sms, quantity: positiveQuantity(input.smsQuantity), baseUsd: smsPrice });
    }

    const controlNumbersPrice = round2(Number(input.controlNumbersPriceUsd || 0));
    if (controlNumbersPrice > 0) {
        addOnBaseUSD = round2(addOnBaseUSD + controlNumbersPrice);
        addOnPlusUSD = round2(addOnPlusUSD + (plus ? round2(controlNumbersPrice * MIX_IVA_RATE) : 0));
        lines.push({
            type: 'controlNumbers',
            label: MIX_LINE_LABELS.controlNumbers,
            quantity: positiveQuantity(input.controlNumbersQuantity),
            baseUsd: controlNumbersPrice,
        });
    }

    const extraUsersToAdd = Math.max(0, Math.floor(Number(input.extraUsersToAdd || 0)));
    const usersMonths = extraUsersToAdd > 0 ? mixExtraUsersMonths(input.referenceDate, input.paymentFrequency, input.now) : 0;
    if (usersMonths > 0) {
        const unit = getExtraUserPriceUsd(input);
        const usersBase = round2(extraUsersToAdd * unit * usersMonths * extraUsersFrequencyFactor(input.paymentFrequency));
        addOnBaseUSD = round2(addOnBaseUSD + usersBase);
        addOnPlusUSD = round2(addOnPlusUSD + (plus ? round2(usersBase * MIX_IVA_RATE) : 0));
        if (usersBase > 0) {
            lines.push({ type: 'users', label: MIX_LINE_LABELS.users, quantity: extraUsersToAdd, baseUsd: usersBase });
        }
    }

    let setupBaseUSD = 0;
    let setupPlusUSD = 0;
    let retentionUSD = 0;
    let setupPayableUSD = 0;
    if (input.includeSetup) {
        const setup = input.setup || {};
        setupBaseUSD = round2(Number(setup.baseAmountUSD || 0));
        setupPlusUSD = setup.ivaUSD != null ? round2(Number(setup.ivaUSD)) : plus ? round2(setupBaseUSD * MIX_IVA_RATE) : 0;
        retentionUSD = round2(Number(setup.retentionUSD || 0));
        setupPayableUSD = setup.payableUSD != null ? round2(Number(setup.payableUSD)) : round2(setupBaseUSD + setupPlusUSD - retentionUSD);
        if (setupBaseUSD > 0) {
            lines.push({ type: 'setup', label: MIX_LINE_LABELS.setup, quantity: null, baseUsd: setupBaseUSD });
        }
    }

    const baseUSD = round2(addOnBaseUSD + setupBaseUSD);
    const plusUSD = round2(addOnPlusUSD + setupPlusUSD);
    const amountUSD = round2(round2(addOnBaseUSD + addOnPlusUSD) + setupPayableUSD);
    const rate = Number(input.exchangeRateBCV || 0);

    return {
        baseUSD,
        plusUSD,
        retentionUSD,
        amountUSD,
        amountVES: rate > 0 ? round2(amountUSD * rate) : 0,
        lines,
    };
}
